// ============================================================================
// One rendering of "everything we know about a truck booking", shared by both
// places that show it:
//   • the Logistics dashboard  — row click → BookingDrawer   (a LogisticsOrderRow)
//   • an OMS order's Route     — leg click → LogisticsDrawer (a LogisticsDocument)
//
// Those are two different payloads for the same thing — the list row arrives
// pre-flattened with order context and only the latest event, the nested
// document carries full addresses, the whole event trail and the TMS deep link.
// Each side normalises into `BookingView` via its adapter below, so labels,
// wording and layout live in ONE place and can't drift apart again.
// ============================================================================
import { EnvironmentOutlined } from "@ant-design/icons";
import { Descriptions, Space, Typography } from "antd";
import { fmtIST, googleMapsUrl } from "../format";
import React from "react";
import { Link } from "react-router-dom";
import { DeliveryStatusTag } from "../logistics/components/logistics_status_tag";
import { LogisticsDocument, LogisticsEvent, LogisticsOrderRow } from "../types";
import FulfilmentProofs from "./fulfilment_proofs";

const PROVIDER_LABEL: Record<string, string> = {
  own_tms: "Own fleet (Loginext)",
  porter: "Porter",
  intercity: "Intercity / part-load",
  supplier_managed: "Supplier delivers",
};

const humanize = (v?: string | null) => (v ? v.replace(/_/g, " ") : "—");
const dash = (v?: string | null) => v || "—";
const providerLabel = (v?: string | null) =>
  (v && PROVIDER_LABEL[v]) || humanize(v);

// The promised pickup slot, as one line: "28 Aug 2026, 09:00–13:00 IST". Collapsed to a single
// time when there is no end (several carriers commit to a moment, not a window), and the date is
// printed once rather than on both halves.
const dispatchWindow = (view: { dispatchStart?: string | null; dispatchEnd?: string | null }) => {
  if (!view.dispatchStart) return "—";
  const day = fmtIST(view.dispatchStart, "DD MMM YYYY");
  const from = fmtIST(view.dispatchStart, "HH:mm");
  const to = view.dispatchEnd ? fmtIST(view.dispatchEnd, "HH:mm") : null;
  return `${day}, ${from}${to ? `–${to}` : ""} IST`;
};

// A booking as this component renders it. Everything optional is genuinely absent on one
// of the two payloads — the row omits it rather than printing an empty field.
export interface BookingView {
  id: number;
  provider: string;
  deliveryStatus?: string | null; // the clean signal we lead with (created → … → delivered / cancelled)
  state?: string | null;
  status?: string | null;
  leg?: string | null;
  from: string;
  to: string;
  toMapUrl?: string | null; // Google Maps search link for the destination (customer site), when known
  vehicle?: string | null;
  driver?: string | null;
  providerRef?: string | null;
  awb?: string | null;
  trackingUrl?: string | null;
  tmsUrl?: string | null; // deep-link into the carrier's own dashboard
  eta?: string | null;
  createdAt?: string | null;
  // The promised pickup window (start, optional end). Both adapters carry it now — an inbound leg
  // often has no ETA at all, so this is the only date on the booking that means anything yet.
  dispatchStart?: string | null;
  dispatchEnd?: string | null;
  // Order context — only the dashboard needs it; on an order page you are already there.
  order?: {
    salesOrderId: number | null;
    soNumber: string | null;
    leadId: string | null;
    clientName: string | null;
  };
  // Full trail when we have it, otherwise just the last thing the carrier told us.
  events?: LogisticsEvent[];
  latestEvent?: { event_code: string; occurred_at: string } | null;
}

// ---------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------

export const fromRow = (r: LogisticsOrderRow): BookingView => ({
  id: r.id,
  provider: providerLabel(r.provider_type),
  deliveryStatus: r.delivery_status,
  state: r.state,
  status: r.status,
  leg: r.operation_type,
  from: dash(r.origin_label),
  to: dash(r.destination_label),
  // Prefer the precise pin the row now carries (from the endpoint snapshot); fall back to a search
  // on the flat label when coordinates are absent.
  toMapUrl: googleMapsUrl(
    { lat: r.destination_lat, long: r.destination_long },
    [r.destination_label]
  ),
  vehicle: r.vehicle_number,
  driver: [r.driver_name, r.driver_number].filter(Boolean).join(" · ") || null,
  providerRef: r.provider_ref,
  awb: r.awb_no,
  trackingUrl: r.tracking_url,
  eta: r.eta,
  createdAt: r.created_at,
  dispatchStart: r.target_dispatch_start,
  dispatchEnd: r.target_dispatch_end,
  order: {
    salesOrderId: r.sales_order_id,
    soNumber: r.zoho_salesorder_number,
    leadId: r.lead_id,
    clientName: r.client_name,
  },
  latestEvent: r.latest_event,
});

// The nested document carries structured addresses rather than a label.
const addr = (a: Record<string, any> | null): string => {
  if (!a) return "—";
  return (
    [a.name, a.address, a.city, a.state, a.pincode].filter(Boolean).join(", ") ||
    JSON.stringify(a)
  );
};

export const fromDocument = (b: LogisticsDocument): BookingView => ({
  id: b.id,
  provider: providerLabel(b.provider_type),
  deliveryStatus: b.delivery_status,
  state: b.state,
  status: b.status,
  leg: b.direction,
  from: addr(b.origin),
  to: addr(b.destination),
  // The endpoint snapshot carries the real lat/long (longitude under `lng`) — pin on it, and fall
  // back to the structured address parts only when coordinates are missing.
  toMapUrl: googleMapsUrl(
    { lat: b.destination?.lat, long: b.destination?.lng },
    [
      b.destination?.address,
      b.destination?.city,
      b.destination?.state,
      b.destination?.pincode,
    ]
  ),
  vehicle: b.vehicle_number,
  driver: [b.driver_name, b.driver_number].filter(Boolean).join(" · ") || null,
  providerRef: b.provider_ref,
  awb: b.awb_no,
  // `tracking_link` is the resolved customer link; `tracking_url` is whatever was stored raw.
  trackingUrl: b.tracking_link || b.tracking_url,
  tmsUrl: b.tms_order_url,
  eta: b.eta,
  createdAt: b.created_at,
  dispatchStart: b.target_dispatch_start,
  dispatchEnd: b.target_dispatch_end,
  events: b.events,
});

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const EventTrail: React.FC<{ view: BookingView }> = ({ view }) => {
  if (!view.events) return null;
  return (
    <>
      <Typography.Text strong style={{ display: "block", margin: "12px 0 8px" }}>
        Updates from the carrier
      </Typography.Text>
      {view.events.length === 0 ? (
        <Typography.Text type="secondary">No updates yet</Typography.Text>
      ) : (
        view.events.map((e, i) => (
          <div key={`${e.event_code}-${i}`} style={{ marginBottom: 8 }}>
            <div>{humanize(e.event_code)}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {fmtIST(e.occurred_at, "DD MMM YYYY HH:mm")} IST
              {e.created_by ? ` · marked by ${e.created_by}` : ""}
            </Typography.Text>
          </div>
        ))
      )}
    </>
  );
};

const BookingDetails: React.FC<{
  view: BookingView;
  adminOnlyProofs?: boolean;
  // Drop the "Order" row — the link out to the order's own page. Opted into by the Vendor
  // Dashboard, whose reader works the trip in front of them and has no business on the order page.
  // Defaults off, so every existing surface keeps the row exactly as it was. Lead and Client are
  // unaffected: they are context on the booking, not a way off this screen.
  hideOrderRow?: boolean;
  // Drop the "Tracking" row — the link out to the carrier's own tracking page. Opted into by the
  // Transportation Vendors board, which reaches carrier tracking through its own embedded view
  // instead. Defaults off, so every existing surface keeps the row.
  hideTrackingRow?: boolean;
}> = ({ view, adminOnlyProofs = false, hideOrderRow = false, hideTrackingRow = false }) => (
  <>
    <Space wrap style={{ marginBottom: 16 }}>
      <DeliveryStatusTag status={view.deliveryStatus} />
    </Space>

    <Descriptions size="small" column={1} bordered>
      {view.order && !hideOrderRow && (
        <Descriptions.Item label="Order">
          {view.order.salesOrderId ? (
            <Link to={`/oms/${view.order.salesOrderId}`}>
              {view.order.soNumber || `SO #${view.order.salesOrderId}`}
            </Link>
          ) : (
            dash(view.order.soNumber)
          )}
        </Descriptions.Item>
      )}
      {view.order && (
        <Descriptions.Item label="Lead">{dash(view.order.leadId)}</Descriptions.Item>
      )}
      {view.order && (
        <Descriptions.Item label="Client">{dash(view.order.clientName)}</Descriptions.Item>
      )}
      <Descriptions.Item label="Carrier">{view.provider}</Descriptions.Item>
      <Descriptions.Item label="Leg">{humanize(view.leg)}</Descriptions.Item>
      <Descriptions.Item label="From">{view.from}</Descriptions.Item>
      <Descriptions.Item label="To">
        {view.to}
        {view.toMapUrl && (
          <a
            href={view.toMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ marginLeft: 8, whiteSpace: "nowrap" }}
          >
            <EnvironmentOutlined /> Map
          </a>
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Vehicle">{dash(view.vehicle)}</Descriptions.Item>
      <Descriptions.Item label="Driver">{dash(view.driver)}</Descriptions.Item>
      <Descriptions.Item label="Carrier's booking number">
        {dash(view.providerRef)}
      </Descriptions.Item>
      <Descriptions.Item label="LR / AWB">
        {view.tmsUrl ? (
          <a href={view.tmsUrl} target="_blank" rel="noopener noreferrer">
            {view.awb || "Open in carrier dashboard"} ↗
          </a>
        ) : (
          dash(view.awb)
        )}
      </Descriptions.Item>
      {!hideTrackingRow && (
        <Descriptions.Item label="Tracking">
          {view.trackingUrl ? (
            <a href={view.trackingUrl} target="_blank" rel="noopener noreferrer">
              Open tracking ↗
            </a>
          ) : (
            "—"
          )}
        </Descriptions.Item>
      )}
      {/* The dates, together and always rendered — an empty row reading "—" tells the operator the
          carrier has not committed yet, which a hidden row does not. */}
      <Descriptions.Item label="Pickup window">
        {dispatchWindow(view)}
      </Descriptions.Item>
      <Descriptions.Item label="Expected arrival">
        {view.eta ? `${fmtIST(view.eta, "DD MMM YYYY HH:mm")} IST` : "—"}
      </Descriptions.Item>
      <Descriptions.Item label="Booked on">
        {view.createdAt ? `${fmtIST(view.createdAt, "DD MMM YYYY HH:mm")} IST` : "—"}
      </Descriptions.Item>
      {/* Only shown when there is no full trail below — otherwise it repeats the first entry. */}
      {!view.events && (
        <Descriptions.Item label="Last update">
          {view.latestEvent ? (
            <span>
              {humanize(view.latestEvent.event_code)}
              <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                {fmtIST(view.latestEvent.occurred_at, "DD MMM YYYY HH:mm")} IST
              </Typography.Text>
            </span>
          ) : (
            "No updates from the carrier yet"
          )}
        </Descriptions.Item>
      )}
    </Descriptions>

    <EventTrail view={view} />

    <FulfilmentProofs docId={view.id} adminOnly={adminOnlyProofs} />
  </>
);

export default BookingDetails;
